import { Injectable, OnInit } from '@angular/core';
import * as CryptoJS from 'crypto-js';
import { BehaviorSubject } from 'rxjs';
import { environment } from 'src/app/environments/environments';

@Injectable({
  providedIn: 'root',
})
export class EncryptionService implements OnInit {
  // Store this securely - consider environment variables or secure configuration
  private readonly secretKey = environment.encryptionKey; // 32 chars for AES-256
  private currentUserSubject = new BehaviorSubject<UserDetailsToken | null>(
    null
  );
  private isInitalized = false;
  public currentUser$ = this.currentUserSubject.asObservable();
  constructor() {
    this.initializeUser();
  }
  ngOnInit(): void {
    if (!this.isInitalized) {
      this.initializeUser();
    }
  }
  initializeUser(): void {
    const userDetailsToken = this.getToken();
    if (userDetailsToken) {
      const decodedUserDetails = this.decrypt(userDetailsToken);
      this.currentUserSubject.next({
        organizationID: decodedUserDetails.OrganizationID,
        organizationName: decodedUserDetails.OrganizationName,
        organizationAddress: decodedUserDetails.OrganizationAddress,
        organizationEmail: decodedUserDetails.OrganizationEmail,
        userID: decodedUserDetails.UserID,
        isAdmin: decodedUserDetails.isAdmin,
        isEmployee: decodedUserDetails.IsEmployee == 'True' ? true : false,
        employeeDepartment: decodedUserDetails.EmployeeDepartment,
        isLocationAccess:
          decodedUserDetails.LocationAccess == '1' ? true : false,
        fullName:
          decodedUserDetails.FirstName + ' ' + decodedUserDetails.LastName,
        firstName: decodedUserDetails.FirstName,
        lastName: decodedUserDetails.LastName,
        phoneNumber: decodedUserDetails.PhoneNumber,
        email: decodedUserDetails.Email,
        address: decodedUserDetails.Address,
        role: decodedUserDetails.Role.toLowerCase(),
        permissions:
          lowerCaseFirstLetterOfKeys(decodedUserDetails.Permissions) || [],
      });
      this.isInitalized = true;
    }
  }
  getToken(): string | null {
    return localStorage.getItem('UserDetails');
  }
  getCurrentUser(): UserDetailsToken | null {
    return this.currentUserSubject.value;
  }

  hasRole(role: string): boolean {
    const user = this.getCurrentUser();
    return user?.role.includes(role) || false;
  }
  encrypt(data: any): string {
    try {
      const jsonString = JSON.stringify(data);
      const encrypted = CryptoJS.AES.encrypt(
        jsonString,
        this.secretKey
      ).toString();
      return encrypted;
    } catch (error) {
      console.error('Encryption failed:', error);
      throw new Error('Failed to encrypt data');
    }
  }

  decrypt(encryptedData: string): any {
    try {
      const decryptedBytes = CryptoJS.AES.decrypt(
        encryptedData,
        this.secretKey
      );
      const decryptedString = decryptedBytes.toString(CryptoJS.enc.Utf8);
      return JSON.parse(decryptedString);
    } catch (error) {
      console.error('Decryption failed:', error);
      throw new Error('Failed to decrypt data');
    }
  }
}

export interface UserDetailsToken {
  organizationID: string;
  organizationName: string;
  organizationAddress: string;
  organizationEmail: string;
  userID: string;
  isAdmin: boolean;
  isEmployee: boolean;
  employeeDepartment: string;
  isLocationAccess: boolean;
  lastName: string;
  firstName: string;
  fullName: string;
  phoneNumber: string;
  email: string;
  address: string;
  role: string;
  permissions: PathPermission[];
}

export interface PathPermission {
  path: string;
  role: string;
  permissions: string[];
}

function lowerCaseFirstLetterOfKeys(data: any): any {
  if (Array.isArray(data)) {
    return data.map((item) => lowerCaseFirstLetterOfKeys(item));
  } else if (typeof data === 'object' && data !== null) {
    return Object.keys(data).reduce((acc: any, key: string) => {
      const newKey = key.charAt(0).toLowerCase() + key.slice(1);
      acc[newKey] = lowerCaseFirstLetterOfKeys(data[key]); // 🔁 recursively handle nested objects/arrays
      return acc;
    }, {});
  }
  return data; // primitive value
}
